import {
  Box,
  Card as MuiCard,
  CardActions as MuiCardActions,
  CardActionsProps as MuiCardActionsProps,
  CardContent as MuiCardContent,
  CardContentProps as MuiCardContentProps,
  CardHeader as MuiCardHeader,
  CardHeaderProps as MuiCardHeaderProps,
  CardProps as MuiCardProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { themeShape } from '@peritasai/ui-shared';
import React from 'react';

/**
 * PeriVerse Themed Card Component
 *
 * A customized card component that follows the PeriVerse design system.
 * Extends Material-UI Card with additional styling and composition.
 */

// Styled card with custom styling
const StyledCard = styled(MuiCard)(({ theme }) => ({
  // Additional custom styles can be added here
  // The theme overrides are already applied through the global theme
}));

export interface CardProps extends MuiCardProps {
  /**
   * The content of the card
   */
  children?: React.ReactNode;
}

export interface CardContentProps extends MuiCardContentProps {
  /**
   * The content of the card content area
   */
  children: React.ReactNode;
}

export interface CardActionsProps extends MuiCardActionsProps {
  /**
   * The content of the card actions area
   */
  children: React.ReactNode;
}

export interface CardHeaderProps extends MuiCardHeaderProps {
  /**
   * The content of the card header
   */
  children?: React.ReactNode;
}

/**
 * PeriVerse Card Component
 *
 * A themed card component that provides consistent styling across the application.
 * Supports all Material-UI Card props with additional PeriVerse-specific styling.
 *
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader title="Card Title" />
 *   <CardContent>
 *     <Typography>Card content goes here</Typography>
 *   </CardContent>
 *   <CardActions>
 *     <Button>Action 1</Button>
 *     <Button>Action 2</Button>
 *   </CardActions>
 * </Card>
 * ```
 */
export const Card: React.FC<CardProps> = ({ children, ...props }) => {
  return (
    <MuiCard
      sx={[
        { borderRadius: `${themeShape.borderRadius.extraLarge}px` },
        { border: '1px solid rgba(0, 0, 0, 0.12)' },
        { boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.08)' },
      ]}
      {...props}
    >
      {children}
    </MuiCard>
  );
};

/**
 * PeriVerse Card Content Component
 *
 * A themed card content component for the main content area of a card.
 */
export const CardContent: React.FC<CardContentProps> = ({
  children,
  ...props
}) => {
  return <MuiCardContent {...props}>{children}</MuiCardContent>;
};

/**
 * PeriVerse Card Actions Component
 *
 * A themed card actions component for action buttons in a card.
 */
export const CardActions: React.FC<CardActionsProps> = ({
  children,
  ...props
}) => {
  return <MuiCardActions {...props}>{children}</MuiCardActions>;
};

/**
 * PeriVerse Card Header Component
 *
 * A themed card header component for the header area of a card.
 */
export const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  title,
  ...props
}) => {
  // If children are provided, render them wrapped in a Box (for complex headers like Actionable/Blocked)
  if (children) {
    return (
      <MuiCardHeader
        // Pass any props intended for the MuiCardHeader root element
        {...props}
        // sx={{
        //   display: 'flex',
        //   alignItems: 'center',
        //   minHeight: '48px',
        //   padding: '16px',
        //   '& .MuiCardHeader-content': {
        //     width: '100%',
        //   },
        // Allow custom sx to override
        //   ...props.sx, // IMPORTANT: Ensure custom sx is applied
        // }}
      >
        {/* Render children inside the content slot */}
        <Box>{children}</Box>
      </MuiCardHeader>
    );
  }

  // If NO children are provided, fall back to the standard MUI title prop
  return (
    <MuiCardHeader
      title={title}
      {...props}
      sx={{
        minHeight: '48px',
        padding: '16px',
        ...props.sx, // Allow custom sx to override
      }}
    />
  );
};

export default Card;
